import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { formatRupiah } from './utils/formatRupiah';

interface OvertimeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recruitment: any;
  saving: boolean;
  onSave: (data: any) => void;
}

export default function OvertimeDialog({
  open,
  onOpenChange,
  recruitment,
  saving,
  onSave,
}: OvertimeDialogProps) {
  const [overtimeForm, setOvertimeForm] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '17:00',
    end_time: '20:00',
    duration_hours: 3,
    rate_per_hour: 0,
    total_amount: 0,
    description: '',
  });

  useEffect(() => {
    if (open && recruitment) {
      // Calculate rate per hour (default calculation)
      const defaultRate = 50000; // Default rate
      const startTime = '17:00';
      const endTime = '20:00';
      const duration = calculateDuration(startTime, endTime);
      setOvertimeForm({
        date: new Date().toISOString().split('T')[0],
        start_time: startTime,
        end_time: endTime,
        duration_hours: duration,
        rate_per_hour: defaultRate,
        total_amount: Math.round(duration * defaultRate),
        description: '',
      });
    }
  }, [open, recruitment]);

  const calculateDuration = (start: string, end: string) => {
    if (!start || !end) return 0;
    const startDate = new Date(`2000-01-01T${start}`);
    const endDate = new Date(`2000-01-01T${end}`);
    let hours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
    if (hours < 0) hours += 24;
    return Math.round(hours * 10) / 10;
  };

  const handleTimeChange = (field: string, value: string) => {
    const newForm = { ...overtimeForm, [field]: value };

    if (field === 'start_time' || field === 'end_time') {
      const duration = calculateDuration(
        field === 'start_time' ? value : overtimeForm.start_time,
        field === 'end_time' ? value : overtimeForm.end_time
      );
      newForm.duration_hours = duration;
      newForm.total_amount = Math.round(duration * overtimeForm.rate_per_hour);
    } else if (field === 'duration_hours') {
      newForm.total_amount = Math.round(parseFloat(value) * overtimeForm.rate_per_hour);
    } else if (field === 'rate_per_hour') {
      newForm.total_amount = Math.round(overtimeForm.duration_hours * parseFloat(value));
    }

    setOvertimeForm(newForm);
  };

  const handleSubmit = () => {
    if (!recruitment || !overtimeForm.date) return;

    const dataToSave = {
      recruitment_id: recruitment.id,
      ...overtimeForm,
      status: 'pending',
    };

    onSave(dataToSave);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Pengajuan Lembur</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Tanggal *</Label>
            <Input
              type="date"
              value={overtimeForm.date}
              onChange={e => setOvertimeForm({ ...overtimeForm, date: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Jam Mulai</Label>
              <Input
                type="time"
                value={overtimeForm.start_time}
                onChange={e => handleTimeChange('start_time', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Jam Selesai</Label>
              <Input
                type="time"
                value={overtimeForm.end_time}
                onChange={e => handleTimeChange('end_time', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Durasi (Jam)</Label>
              <Input
                type="number"
                step="0.1"
                value={overtimeForm.duration_hours}
                onChange={e => handleTimeChange('duration_hours', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Tarif per Jam</Label>
              <Input
                type="number"
                value={overtimeForm.rate_per_hour}
                onChange={e => handleTimeChange('rate_per_hour', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2 bg-orange-50 p-4 rounded-lg">
            <Label className="text-orange-700">Total Bonus Lembur</Label>
            <p className="text-2xl font-bold text-orange-600">
              Rp {formatRupiah(overtimeForm.total_amount)}
            </p>
            <p className="text-xs text-gray-600">
              Formula: {overtimeForm.duration_hours}h × Rp{' '}
              {formatRupiah(overtimeForm.rate_per_hour)}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Keterangan</Label>
            <Input
              value={overtimeForm.description}
              onChange={e => setOvertimeForm({ ...overtimeForm, description: e.target.value })}
              placeholder="Alasan lembur"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Batal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !overtimeForm.date}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Ajukan Lembur
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
